import React from "react";

import {
  ClipboardList,
  DocumentText,
} from "../../../components/icons/svg/outline";
import {
  emptyRfiCardSummary,
  emptyTmCardSummary,
  PROJECT_CARD_RFI_STATUS_LABELS,
} from "../shared/projectTicketSummaries";

/** Text colors aligned with the status pill tones (not pill backgrounds). */
const STATUS_TEXT_COLORS = {
  gray: "text-gray-600",
  yellow: "text-yellow-700",
  green: "text-green-700",
};

/**
 * One status count in a T&M / RFI row.
 *
 * @param {Object} props
 * @param {string} props.label - Status label.
 * @param {number} props.count - Status count (caller only passes counts &gt; 0).
 * @param {"gray"|"yellow"|"green"} props.tone - Color tone matching pill palette.
 * @returns {JSX.Element}
 */
function StatusCountText({ label, count, tone }) {
  const toneClass = STATUS_TEXT_COLORS[tone] || STATUS_TEXT_COLORS.gray;
  return (
    <span className={`inline-flex items-baseline gap-1 text-xs ${toneClass}`}>
      <span className="font-medium">{label}</span>
      <span className="font-bold tabular-nums">{count}</span>
    </span>
  );
}

/**
 * Compact T&M / RFI summary row with a leading icon.
 *
 * Actionable statuses (count &gt; 0) render as colored counts. When none are
 * outstanding, the row stays as a quiet icon + title only (keeps card height).
 *
 * @param {Object} props
 * @param {string} props.title - Module title (e.g. ``T&M``, ``RFI``).
 * @param {React.ComponentType<{className?: string}>} props.Icon - Leading icon.
 * @param {string} props.iconClassName - Icon color when there are actionable counts.
 * @param {Array<{ label: string, count: number, tone: "gray"|"yellow"|"green" }>} props.pills - Status counts.
 * @param {boolean} [props.isLoading=false] - Whether ticket data is still loading.
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element}
 */
export const CompactTicketSummary = ({
  title,
  Icon,
  iconClassName,
  pills,
  isLoading = false,
  className = "",
}) => {
  const visibleRows = (pills || []).filter((pill) => Number(pill.count) > 0);
  const isIdle = !isLoading && visibleRows.length === 0;

  return (
    <div
      className={`flex min-w-0 items-center gap-2 ${className}`}
      title={isIdle ? "No outstanding items" : undefined}
    >
      <Icon
        className={`h-4 w-4 flex-shrink-0 ${
          isIdle ? "text-gray-300" : iconClassName
        }`}
        aria-hidden
      />
      <span
        className={`flex-shrink-0 text-xs font-bold ${
          isIdle ? "text-gray-400" : "text-gray-900"
        }`}
      >
        {title}
      </span>
      {isLoading ? (
        <span className="text-xs text-gray-400">Loading…</span>
      ) : visibleRows.length > 0 ? (
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
          {visibleRows.map((pill) => (
            <StatusCountText
              key={pill.label}
              label={pill.label}
              count={Number(pill.count) || 0}
              tone={pill.tone}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

/**
 * T&M row: Created (gray), Sent (yellow), Signed (green).
 * Idle (no outstanding) shows icon + title only.
 *
 * @param {Object} props
 * @param {Object} [props.summary] - Compact T&M status summary.
 * @param {boolean} [props.isLoading=false] - Whether ticket lists are still loading.
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element}
 */
export function ProjectTmSummaryTile({
  summary = emptyTmCardSummary(),
  isLoading = false,
  className = "",
}) {
  return (
    <CompactTicketSummary
      title="T&M"
      Icon={ClipboardList}
      iconClassName="text-emerald-700"
      pills={[
        { label: "Created", count: summary.open, tone: "gray" },
        { label: "Sent", count: summary.sent, tone: "yellow" },
        { label: "Signed", count: summary.signed, tone: "green" },
      ]}
      isLoading={isLoading}
      className={className}
    />
  );
}

/**
 * RFI row: Open + Reviewing (gray), Responding (yellow).
 * Idle (no outstanding) shows icon + title only.
 *
 * @param {Object} props
 * @param {Object} [props.summary] - Compact RFI status summary.
 * @param {boolean} [props.isLoading=false] - Whether ticket lists are still loading.
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element}
 */
export function ProjectRfiSummaryTile({
  summary = emptyRfiCardSummary(),
  isLoading = false,
  className = "",
}) {
  return (
    <CompactTicketSummary
      title="RFI"
      Icon={DocumentText}
      iconClassName="text-sky-700"
      pills={[
        {
          label: PROJECT_CARD_RFI_STATUS_LABELS.open,
          count: summary.open,
          tone: "gray",
        },
        {
          label: PROJECT_CARD_RFI_STATUS_LABELS.reviewing,
          count: summary.reviewing,
          tone: "gray",
        },
        {
          label: PROJECT_CARD_RFI_STATUS_LABELS.responding,
          count: summary.responding,
          tone: "yellow",
        },
      ]}
      isLoading={isLoading}
      className={className}
    />
  );
}

export default CompactTicketSummary;
